import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { formatNumber } from '@bookingdei/i18n/format-number';
import { useLanguage } from '../../shell/language-context';
import { offerEnds, offerTarget, offerPercent } from './home-model';
import { HomeIcon } from './home-icons';
import { useScrollEdges } from '../bookings/use-scroll-edges';
import type { OfferCard } from '../../mock/seeds/schema-types';

// "New offers near you" (customer gap §A.3 H6; Home.dc.html L159-230): up to ten offer
// cards on a horizontal, swipeable slider. The design's auto-scrolling marquee rendered
// the list twice and, with fewer cards than the row is wide, showed the copies and a
// wide empty run while it looped — it is now one list on a native scroller with
// scroll-snap (no duplicate cards, nothing moves on its own), the first card on the page
// gutter and the row bleeding to the screen edge on phones. From 760 px the cards size
// to three / four across and Previous / More buttons page the row when it overflows.
// A paid placement carries a visible "Sponsored" marker (AP-D10, FR-A18) — the design
// has none (gap §G Q17). A card that names its service (`catalog_item_slug`) opens the
// in-app wizard on it with the offer (DC63, the design's `?new=service:<id>&offer=<id>`);
// a whole-business offer opens the business's page on the website (DR-40: /b/{slug}).
export function HomeOffers({
  offers,
  websiteOrigin,
}: {
  offers: OfferCard[];
  websiteOrigin: string;
}) {
  const { t } = useLanguage();
  const rowRef = useRef<HTMLUListElement>(null);
  const edges = useScrollEdges(rowRef, offers.length);
  const page = (dir: 1 | -1) => {
    const el = rowRef.current;
    if (!el) return;
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: reduce ? 'auto' : 'smooth' });
  };
  return (
    <section className="hm-offers" aria-labelledby="hm-offers-title" data-motion="">
      <div className="hm-sec-head">
        <span className="hm-sec-head__title" id="hm-offers-title">
          {t('user.home.offers.title')}
        </span>
        {(edges.start || edges.end) && (
          <span className="hm-offers__nav">
            <button type="button" className="hm-offers__arrow" aria-label={t('user.home.offers.prev')} disabled={!edges.start} onClick={() => page(-1)}>
              <HomeIcon name="chevron" className="hm-offers__arrow-back" />
            </button>
            <button type="button" className="hm-offers__arrow" aria-label={t('user.home.offers.next')} disabled={!edges.end} onClick={() => page(1)}>
              <HomeIcon name="chevron" />
            </button>
          </span>
        )}
        <a className="hm-sec-head__link" href={`${websiteOrigin}/offer`}>
          {t('user.home.offers.all')}
        </a>
      </div>
      <ul className="hm-carousel" ref={rowRef}>
        {offers.map((o) => (
          <li key={o.id} className="hm-carousel__item">
            <HomeOfferCard offer={o} websiteOrigin={websiteOrigin} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function HomeOfferCard({
  offer: o,
  websiteOrigin,
}: {
  offer: OfferCard;
  websiteOrigin: string;
}) {
  const { t, lang } = useLanguage();
  const ends = offerEnds(o);
  // DR-77: only a bookable listing opens the wizard; others go to their (contact-only) page.
  const target = offerTarget(o, websiteOrigin);
  const bizName = (lang === 'bn' ? o.business.name_bn : undefined) ?? o.business.name;
  const place = [bizName, o.business.area_name].filter(Boolean).join(' — ');
  const photo = o.image?.['400'] ?? Object.values(o.image ?? {})[0];
  return (
    <Link
      className="hm-offer"
      to={target.href}
      reloadDocument={!target.internal}
      // The wizard closes back to Home rather than to My bookings.
      state={target.internal ? { from: '/' } : undefined}
    >
      <span className="hm-offer__media">
        {photo && (
          <img
            className="hm-offer__photo"
            src={photo}
            alt=""
            width={236}
            height={120}
            loading="lazy"
            decoding="async"
          />
        )}
        <span className="hm-offer__shade" aria-hidden="true" />
        {o.sponsored && <span className="hm-offer__sponsored">{t('common.sponsored')}</span>}
        {ends && (
          <span className="hm-offer__ends">
            <HomeIcon name="clock" size={12} />
            {ends.days === 0
              ? t('user.home.offers.ends_today')
              : formatMessage(lang, 'user.home.offers.days_left', { n: ends.days })}
          </span>
        )}
        <span className="hm-offer__pct">
          <span className="hm-offer__pct-n">{formatNumber(lang, offerPercent(o))}%</span>
          <span className="hm-offer__pct-w">{t('user.home.offers.off')}</span>
        </span>
      </span>
      <span className="hm-offer__body">
        <span className="hm-offer__title">{o.title}</span>
        <span className="hm-offer__biz">{place}</span>
        <span className="hm-offer__view">
          {t('user.home.offers.view')}
          <HomeIcon name="arrow" />
        </span>
      </span>
    </Link>
  );
}
